/**
 * #191 quick-batch — plan-mode full-screen view.
 *
 * Replaces the normal chat panel in `<Workspace>` whenever
 * `interview.subPhase` is `'plan'`, `'refine'`, or `'back-to-plan'`.
 * Renders the SKILL-authored plan markdown as the main content,
 * exposes a chat input at the bottom for refinements, and ships a
 * prominent "Start Building" CTA that fires `interview:start-building`
 * via IPC.
 *
 * Design choices (per the #191 spec):
 *
 *   - Activated by parent (`<Workspace>`) gating on
 *     `interview.depth === 'quick'` AND a matching subPhase. This
 *     component renders the surface regardless of the state values —
 *     visibility is the parent's call.
 *   - Refinement input is plain text routed through the existing
 *     `agent:send` IPC path. The SKILL recognises refinement turns
 *     while `subPhase === 'plan'` and rewrites the top-level `plan`
 *     markdown (per the canonical schema in #543; no version envelope).
 *     The renderer picks the update up via the `interview:plan-updated`
 *     push — content-hash dedup keeps duplicate writes from triggering
 *     a refetch storm.
 *   - "Start over" link delegates to the parent (Workspace) so the
 *     archive/abandon logic stays in one place (`useStartOverInterview`).
 *   - Markdown rendering deliberately conservative: paragraph splits
 *     + `<pre>` blocks for indented sections, no full markdown parser.
 *     The SKILL emits a structured plan with simple headings; we
 *     keep the bundle lean (no `marked` / `remark` dep just for this
 *     surface). A future iteration can swap in a richer renderer.
 *
 * Crucible #823 — transparency heartbeat for the drafting state. While
 * `plan` is null/empty the surface previously showed a fully static
 * panel for the entire drafting window (observed 3m+ by the user). This
 * violates the ≤5s rule from #827. Three motion elements address it:
 *
 *   1. Animated trailing dots on the headline (`animated-ellipsis`
 *      keyframe from globals.css, established in PR #805).
 *   2. A horizontal shimmer band sweeping the placeholder area
 *      (`plan-drafting-shimmer` keyframe, defined here in globals.css).
 *   3. A 1Hz elapsed timer ("Working for 0:23…") using `useElapsedTick`
 *      and `formatElapsedMs` from `ElapsedTickSpan` — real state change,
 *      top of the #827 heartbeat preference list.
 *
 * All three are gated on `draftingStartedAt` being non-null so the
 * motion only fires during an active drafting pass. Reduced-motion:
 * shimmer + dots degraded via `@media (prefers-reduced-motion: reduce)`
 * in globals.css; the timer always ticks regardless (it is text, not
 * animation).
 */

import { useCallback, useState, type FormEvent, type ReactElement } from 'react';
import { useElapsedTick } from '../hooks/useElapsedTick';
import { formatElapsedMs } from './ElapsedTickSpan';
import { MarkdownContent } from './MarkdownContent';
import type { KnownInterviewSubPhase } from '@volley/crucible-ipc-types/build-game-types';

export const PLAN_MODE_TESTID = 'plan-mode-view';
export const PLAN_MODE_BODY_TESTID = 'plan-mode-body';
export const PLAN_MODE_REFINE_INPUT_TESTID = 'plan-mode-refine-input';
export const PLAN_MODE_REFINE_SUBMIT_TESTID = 'plan-mode-refine-submit';
export const PLAN_MODE_START_BUILDING_TESTID = 'plan-mode-start-building';
export const PLAN_MODE_START_OVER_TESTID = 'plan-mode-start-over';
export const PLAN_MODE_EMPTY_TESTID = 'plan-mode-empty';
export const PLAN_MODE_ELAPSED_TESTID = 'plan-mode-elapsed';
export const PLAN_MODE_SHIMMER_TESTID = 'plan-mode-shimmer';

/**
 * Sub-phase values `<PlanModeView>` cares about. The canonical schema
 * (`KnownInterviewSubPhase`) carries six values including `'quick'`
 * (mid-bulk-pitch turn), `'committed'` (after Start Building), and
 * `'build'` (Phase B running) — none of which the plan-mode view
 * should be mounted under. We narrow the prop so the parent's gate
 * is documented at the type level.
 */
export type PlanModeViewSubPhase = Extract<
  KnownInterviewSubPhase,
  'plan' | 'refine' | 'back-to-plan'
>;

export interface PlanModeViewProps {
  /** The gameId — surfaced as data attribute for e2e selectors. */
  gameId: string;
  /**
   * SKILL-authored plan markdown body. Empty / undefined while the
   * SKILL is composing the first revision — the renderer paints
   * the "Drafting your plan..." empty state. Canonical schema
   * carries `plan` as a plain string (sibling of `depth` /
   * `subPhase`) — no version / updatedAt envelope.
   */
  plan: string | null | undefined;
  /** Sub-phase value driving the surface — drives the loading copy. */
  subPhase: PlanModeViewSubPhase;
  /** True while a refinement turn is in flight (chat input shows a busy state). */
  refinementBusy?: boolean;
  /** True while the Start Building IPC is in flight. */
  startBuildingBusy?: boolean;
  /**
   * Crucible #823 — epoch ms when the plan-drafting pass started.
   * Comes from the `__main__` agent's `startedAt` in the activity
   * snapshot. When non-null AND `plan` is empty, the drafting empty
   * state shows an elapsed timer ("Working for 0:23…") ticking at
   * 1Hz alongside the shimmer + animated-dots motion elements. When
   * null the empty state is inert (no active build in flight, or
   * feature-flag off).
   */
  draftingStartedAt?: number | null;
  /**
   * Crucible #823 (Codex round-1 HIGH) — epoch ms when the main
   * orchestrator agent stopped (reached a terminal state). Sourced
   * from `ActivitySnapshot.byAgentId['__main__'].stoppedAt`. When
   * non-null, the elapsed timer freezes at that value rather than
   * continuing to tick — the linger window after agent completion
   * must not keep advancing the counter. Defaults to null (running).
   */
  draftingStoppedAt?: number | null;
  /** Fires when the user submits a refinement request. */
  onSubmitRefinement: (text: string) => void;
  /** Fires when the user clicks the prominent "Start Building" CTA. */
  onStartBuilding: () => void;
  /** Fires when the user clicks the "Start over" link. Parent invokes `/abandon`. */
  onStartOver: () => void;
}

/**
 * Crucible #823 — isolated 1Hz elapsed-timer leaf for the drafting
 * empty state. Isolation mirrors `<ElapsedTickSpan>` (design §9 Rule 3):
 * the tick lives here so the per-second re-render never propagates to
 * the memoised `<PlanModeView>` parent.
 *
 * `formatElapsedMs` formats as "12s" / "4m 12s". Here we prepend
 * "Working for " and append "…" to humanise the counter. The timer
 * stops updating once `stoppedAt` is non-null (agent terminal).
 */
function DraftingElapsedTimer({
  startedAt,
  stoppedAt,
}: {
  startedAt: number;
  stoppedAt: number | null;
}): ReactElement {
  const isRunning = stoppedAt === null;
  const tick = useElapsedTick(isRunning);
  void tick; // re-render trigger; actual value ignored
  const elapsed = formatElapsedMs(startedAt, stoppedAt);
  return (
    <p
      className="mt-3 text-xs tabular-nums text-white/30"
      data-testid={PLAN_MODE_ELAPSED_TESTID}
    >
      Working for {elapsed}…
    </p>
  );
}

/**
 * Render the plan-body markdown with deliberate minimalism. The SKILL
 * emits plans as a sequence of headings + bullets + short paragraphs;
 * any future richer markdown can swap this for a real renderer
 * without touching the rest of `<PlanModeView>`.
 */
export function PlanBody({ body }: { body: string }): ReactElement {
  return (
    <MarkdownContent
      content={body}
      testId={PLAN_MODE_BODY_TESTID}
      className="space-y-4 text-sm leading-relaxed text-white/80"
    />
  );
}

export function PlanModeView({
  gameId,
  plan,
  subPhase,
  refinementBusy,
  startBuildingBusy,
  draftingStartedAt,
  draftingStoppedAt = null,
  onSubmitRefinement,
  onStartBuilding,
  onStartOver,
}: PlanModeViewProps): ReactElement {
  const [refineText, setRefineText] = useState('');

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const trimmed = refineText.trim();
      if (!trimmed) return;
      onSubmitRefinement(trimmed);
      setRefineText('');
    },
    [refineText, onSubmitRefinement],
  );

  // Sub-phase drives the busy/loading copy at the top of the surface.
  // The narrowed prop union (`PlanModeViewSubPhase`) only covers the
  // three values the plan-mode view is mounted under.
  const subPhaseLabel: Record<PlanModeViewSubPhase, string> = {
    plan: 'Plan ready for review',
    refine: 'Refining the plan...',
    'back-to-plan': 'Paused — refining the plan',
  };

  return (
    <section
      data-testid={PLAN_MODE_TESTID}
      data-game-id={gameId}
      data-sub-phase={subPhase}
      aria-labelledby="plan-mode-heading"
      className="flex flex-1 flex-col overflow-hidden bg-crucible-surface"
    >
      {/* Header */}
      <header className="flex items-center justify-between border-b border-white/10 px-6 py-4">
        <div>
          <h2 id="plan-mode-heading" className="text-base font-semibold text-white">
            Plan
          </h2>
          <p className="mt-0.5 text-xs text-white/40">{subPhaseLabel[subPhase]}</p>
        </div>
        <button
          type="button"
          onClick={onStartOver}
          data-testid={PLAN_MODE_START_OVER_TESTID}
          className="text-xs text-white/40 underline-offset-2 hover:text-white/70 hover:underline"
        >
          Start over
        </button>
      </header>

      {/* Plan body — scroll region */}
      <div className="flex-1 overflow-auto px-6 py-6">
        {plan && plan.length > 0 ? (
          <PlanBody body={plan} />
        ) : (
          <div
            data-testid={PLAN_MODE_EMPTY_TESTID}
            className="flex h-full flex-col items-center justify-center text-center text-sm text-white/50"
          >
            {/* Motion element 1 — animated trailing dots on the headline.
                Reuses the `animated-ellipsis` pseudo-element keyframe from
                globals.css (established in PR #805). The text node ends
                without literal dots so the pseudo-content doesn't double
                them.
                `animated-ellipsis` is ALWAYS applied on the drafting
                headline — reduced-motion degrades to static `...` and the
                inert (no active build) path still gets dots via the
                `::after` pseudo-element. Without this the backwards-
                compatible fallback (no `draftingStartedAt` prop) would
                render "Drafting your plan" with no trailing indicator at
                all. Bugbot round-1 Medium. Crucible #823 / #827. */}
            <p className="animated-ellipsis font-medium text-white/70">
              Drafting your plan
            </p>
            {/* Motion element 2 — shimmer band sweeping the placeholder
                area. The `plan-drafting-shimmer` CSS class is defined in
                globals.css (added in this PR). Gated on `draftingStartedAt`
                so the shimmer only runs during an active build. Reduced-
                motion fallback in globals.css drops the sweep animation and
                shows a static low-alpha bar instead. Crucible #823. */}
            {draftingStartedAt != null && (
              <div
                data-testid={PLAN_MODE_SHIMMER_TESTID}
                className="plan-drafting-shimmer mt-4 h-1 w-48 rounded-full"
                aria-hidden="true"
              />
            )}
            <p className="mt-3 text-xs text-white/40">
              The agent is composing a plan from your answers.{' '}
              This usually takes 30–90 seconds — longer for complex games.
            </p>
            {/* Motion element 3 — 1Hz elapsed timer. Real state change;
                top of the #827 heartbeat preference list. Isolated to
                `<DraftingElapsedTimer>` so the per-second tick never
                triggers a re-render of the memoised parent (design §9
                Rule 3 pattern, same as `<ElapsedTickSpan>`). Only
                rendered when `draftingStartedAt` is non-null. Crucible
                #823.
                Codex round-1 HIGH: pass `draftingStoppedAt` (from the
                `__main__` agent's terminal `stoppedAt`) so the timer
                freezes when the orchestrator reaches a terminal state
                rather than continuing to tick through the activity-row
                linger window. */}
            {draftingStartedAt != null && (
              <DraftingElapsedTimer
                startedAt={draftingStartedAt}
                stoppedAt={draftingStoppedAt}
              />
            )}
          </div>
        )}
      </div>

      {/* CTA row — Start Building */}
      <div className="border-t border-white/10 px-6 py-4">
        <button
          type="button"
          onClick={onStartBuilding}
          disabled={!plan || plan.length === 0 || startBuildingBusy}
          data-testid={PLAN_MODE_START_BUILDING_TESTID}
          className="w-full rounded-lg bg-crucible-accent px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Start Building
        </button>
        <p className="mt-2 text-center text-xs text-white/40">
          Happy with the plan? Click to dispatch specialists and begin Phase B.
        </p>
      </div>

      {/* Refinement chat input */}
      <form
        onSubmit={handleSubmit}
        className="border-t border-white/10 bg-black/20 px-6 py-3"
      >
        <label className="mb-1 block text-xs text-white/40" htmlFor="plan-mode-refine">
          Refine the plan
        </label>
        <div className="flex gap-2">
          <input
            id="plan-mode-refine"
            type="text"
            value={refineText}
            onChange={(e) => setRefineText(e.target.value)}
            placeholder="e.g. add a 4-player co-op mode, or swap the soundtrack for chiptune..."
            disabled={refinementBusy}
            data-testid={PLAN_MODE_REFINE_INPUT_TESTID}
            className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-crucible-accent disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={refinementBusy || refineText.trim().length === 0}
            data-testid={PLAN_MODE_REFINE_SUBMIT_TESTID}
            className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/20 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {refinementBusy ? 'Refining...' : 'Send'}
          </button>
        </div>
      </form>
    </section>
  );
}
